/* eslint-disable import/no-duplicates */
/* eslint-disable no-console */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { parseISO, format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { Form } from '@unform/web';

import { formatInTimeZone } from 'date-fns-tz';
import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import Toast from '~/utils/toast';

import {
  Action,
  Balance,
  Cashflow,
  Container,
  Extract,
  Filter,
  Modal,
} from './styles';
import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import Select, { IOption } from '~/components/Select';
import { KrClose, KrEye, KrEyeSlash, KrInfo } from '~/components/KoroIcons';
import InputMask from '~/components/InputMask';

import ticketSold from '~/assets/icons/ticket-sold-new.svg';
import Loading from '~/components/Loading';
import KrWithdraw from '~/components/KoroIcons/KrWithdraw';
import Pagination from '~/components/Pagination';

interface IStatus {
  id: number;
  name: string;
}

interface IEventType {
  id: number;
  name: string;
}

interface IExtractData {
  id: number;
  organizer_id: number;
  created_at: string;
  type: string;
  doc_number?: string;
  payment_type?: string;
  value: string;
  event_id?: number;
  user_name?: string;
  retained: string;
  balance: string;
  archive_url?: string;
}

interface IExtractResponse {
  data: IExtractData[];
  from: number;
  pages: number;
  to: number;
  total: number;
}

interface IExtract {
  orderUserName: string;
  eventId: number;
  paymentType: string;
  orderId?: number | string;
  number: string;
  date: string;
  hour: string;
  action: string;
  value: string;
  balance: string;
  total: string;
  // cancelled: string;
  receipt?: string;
}

interface ICashFlowResponse {
  month: number;
  year: number;
  accumulated: number;
  received: number;
  drawn: number;
  receivables: number;
  balance: number;
  cancelled: number;
}

interface ICashFlow {
  date: string;
  accumulated: string;
  received: string;
  drawn: string;
  receivables: string;
  balance: string;
  cancelled: string;
}

const ExtractType = {
  VENDA: ['Venda', 'Liberação'],
  SAQUE: [
    'Solicitação de saque',
    'Transferência realizada',
    'Estorno de saque',
  ],
  CANCELAMENTO: [
    'Solicitação cancelamento',
    'Cancelamento',
    'Saque de reembolso',
    'Cancelamento realizado',
    'Estorno de cancelamento',
  ],
};

const Financial: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const formRefEvent = useRef<FormHandles>(null);
  const [available, setAvailable] = useState(0);
  const [, setTotal] = useState(0);
  const [retained, setRetained] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState<IOption[]>([]);
  const [status, setStatus] = useState<IOption[]>([]);
  const [showValues, setShowValues] = useState(true);
  const [cashesFlow, setCashesFlow] = useState<ICashFlow[]>([]);
  const [extracts, setExtracts] = useState<IExtract[]>([]);
  const [withdraw, setWithdraw] = useState<number>(0);
  const [withdrawError, setWithdrawError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageSelected, setPageSelected] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [eventsModal, setEventsModal] = useState<IOption[]>([]);
  const [eventName, setEventName] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(eventName);
  const [, setOrganizerId] = useState<number | null>(null);
  const [eventsList, setEventsList] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const data = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(data);
    });
  }, []);

  useEffect(() => {
    api
      .get<IEventType[]>('events-types', { params: { isOrganizer: true } })
      .then((response) => {
        const data = response.data.map<IOption>((modality) => ({
          id: modality.id,
          value: modality.name,
          selected: false,
        }));

        setCategories(data);
      });
  }, []);

  useEffect(() => {
    api.get('/wallets/organizers').then((response) => {
      setAvailable(parseFloat(response.data.available));
      setTotal(parseFloat(response.data.total));
      setRetained(parseFloat(response.data.retained));
    });
  }, []);
  console.log(available, retained);

  const handleLoadEventsModal = useCallback(async (search = '') => {
    try {
      const response = await api.get('events/organizer/dropdown', {
        params: {
          search: search.trim(),
          status_ids: [3, 4, 5, 6, 7, 8, 9],
        },
      });
      const data = response.data
        .map((event: any) => ({
          id: event?.id,
          value: event?.name,
          selected: false,
        }))
        .sort((a: IOption, b: IOption) => a.value.localeCompare(b.value));
      setOrganizerId(response.data[0]?.organizer_id);
      setEventsList(response.data);
      setEventsModal([
        {
          id: '',
          value: 'Todos',
          selected: true,
        } as IOption,
        ...data,
      ]);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    handleLoadEventsModal(debouncedSearch);
  }, [debouncedSearch, handleLoadEventsModal]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(eventName);
    }, 500);

    return () => clearTimeout(timer);
  }, [eventName]);

  const loadEventData = useCallback(async (event: any) => {
    if (event) {
      try {
        setLoading(true);
        const response = await api.get(`events/${event.slug}`);

        console.log(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const handleChangeTextEvent = useCallback((text) => {
    setEventName(text === 'Todos' ? '' : text);
  }, []);

  const handleSubmitEvent = useCallback(async () => {
    loadEventData(selectedEvent);
  }, [selectedEvent, loadEventData]);

  const handleLoadCashFlow = useCallback((event_id?: number) => {
    if (event_id) {
      api
        .get<ICashFlowResponse[]>(`cash-flow-events/events/${event_id}`)
        .then((response) => {
          const cashFlow = response.data
            .reverse()
            .map<ICashFlow>((cashFlowData) => {
              const date = format(
                new Date(cashFlowData.year, cashFlowData.month - 1),
                'MMM/yyyy',
                {
                  locale: ptBR,
                }
              );
              return {
                date: `${date.slice(0, 1).toUpperCase()}${date.slice(1, 8)}`,
                accumulated: formatPrice(cashFlowData.accumulated),
                receivables: formatPrice(cashFlowData.receivables),
                balance: formatPrice(cashFlowData.balance),
                drawn: formatPrice(cashFlowData.drawn),
                received: formatPrice(cashFlowData.received),
                cancelled: formatPrice(cashFlowData.cancelled || 0),
              };
            });

          setAvailable(
            parseFloat(
              cashFlow[0].balance
                .replace('R$', '')
                .replace('.', '')
                .replace(',', '.')
            )
          );
          setCashesFlow(cashFlow);
        });
    } else {
      api.get<ICashFlowResponse[]>('cash-flows/organizer').then((response) => {
        const cashFlow = response.data
          .reverse()
          .map<ICashFlow>((cashFlowData) => {
            const date = format(
              new Date(cashFlowData.year, cashFlowData.month - 1),
              'MMM/yyyy',
              {
                locale: ptBR,
              }
            );
            return {
              date: `${date.slice(0, 1).toUpperCase()}${date.slice(1, 8)}`,
              accumulated: formatPrice(cashFlowData.accumulated),
              receivables: formatPrice(cashFlowData.receivables),
              balance: formatPrice(cashFlowData.balance),
              drawn: formatPrice(cashFlowData.drawn),
              received: formatPrice(cashFlowData.received),
              cancelled: formatPrice(cashFlowData.cancelled || 0),
            };
          });

        setAvailable(
          parseFloat(
            cashFlow[0].balance
              .replace('R$', '')
              .replace('.', '')
              .replace(',', '.')
          )
        );
        setCashesFlow(cashFlow);
      });
    }
  }, []);

  useEffect(() => {
    handleLoadCashFlow();
  }, [handleLoadCashFlow]);

  const handleLoadExtracts = useCallback(
    async (
      page: number,
      event?: string,
      status_id?: string,
      type_id?: string,
      dateStart?: string,
      dateEnd?: string,
      event_id?: number
    ) => {
      setLoading(true);
      const response = await api
        .get<IExtractResponse>('/extracts/organizers', {
          params: {
            page,
            event,
            status_id,
            type_id,
            dateStart: dateStart ? new Date(dateStart) : undefined,
            dateEnd: dateEnd ? new Date(dateEnd) : undefined,
            event_id,
          },
        })
        .finally(() => {
          setLoading(false);
        });
      //
      const timeZone = 'Etc/GMT';

      const data = response.data.data.reverse().map<IExtract>((extract) => {
        let paymentType = '-';

        if (extract.payment_type) {
          if (extract.payment_type.toLowerCase().includes('pix')) {
            paymentType = 'Pix';
          }
          if (extract.payment_type.toLowerCase().includes('credit')) {
            paymentType = 'Crédito';
          }
          if (extract.payment_type.toLowerCase().includes('free')) {
            paymentType = 'Gratuito';
          }
        }

        return {
          number: extract.id.toString().padStart(6, '0'),
          date: formatInTimeZone(
            parseISO(extract.created_at),
            timeZone,
            'dd/MM'
          ),
          hour: formatInTimeZone(
            parseISO(extract.created_at),
            timeZone,
            'HH:mm'
          ),
          action: extract.type,
          value: formatPrice(parseFloat(extract.value)),
          orderId: extract.doc_number || '-',
          paymentType,
          total: formatPrice(parseFloat(extract.retained)),
          eventId: extract.event_id || 0,
          orderUserName: extract.user_name || '-',
          balance: formatPrice(parseFloat(extract.balance)),
          // cancelled: formatPrice(parseFloat(extract.cancelled)),
          receipt: extract.archive_url,
        };
      });

      setTotalPages(response.data.pages);
      setExtracts(data.reverse());
    },
    []
  );

  useEffect(() => {
    handleLoadExtracts(1);
  }, [handleLoadExtracts]);

  const handleSelectEvent = useCallback(
    (event: IOption) => {
      if (event.id) {
        handleLoadCashFlow(parseInt(event.id.toString(), 10));
        handleLoadExtracts(
          1,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          parseInt(event.id.toString(), 10)
        );
      } else {
        handleLoadCashFlow();
        handleLoadExtracts(1);
      }
      const eventFound = eventsList.find((e) => e.id === Number(event.id));
      setSelectedEvent(eventFound);
      setEventName(event.value === 'Todos' ? '' : event.value);
      setSelectedEventId(Number(event.id));
      // loadEventData(eventFound);
    },
    [eventsList, handleLoadCashFlow, handleLoadExtracts]
  );

  const columns = useMemo(
    () =>
      [
        {
          name: '',
          selector: 'date',
          sortable: false,
        },
        {
          name: 'Acumulado',
          selector: 'accumulated',
          sortable: false,
          toolTip:
            'Este é o valor total das vendas de inscrições em reais, somando todas as vendas realizadas até o momento.',
        },
        {
          name: 'A Receber',
          selector: 'receivables',
          sortable: false,
          toolTip:
            'Este é o valor que ainda falta ser recebido na sua conta. É a diferença entre o total Acumulado e o total Recebido.',
        },
        {
          name: 'Recebido',
          selector: 'received',
          sortable: false,
          toolTip:
            'Esse valor representa o total que já foi liberado para sua conta na KORO.',
        },
        {
          name: 'Disponível',
          selector: 'balance',
          sortable: false,
          toolTip:
            'Este é o valor que já foi recebido na sua conta, mas que ainda não foi sacado. É calculado como o total Recebido menos o total Sacado.',
        },
        {
          name: 'Sacado',
          selector: 'drawn',
          sortable: false,
          toolTip:
            'Aqui está o valor total em reais que você já retirou da sua conta na KORO através de saques.',
        },
        {
          name: 'Cancelado',
          selector: 'cancelled',
          sortable: false,
          toolTip:
            'Aqui está o valor total em reais que foi solicitado para cancelamento de vendas.',
        },
      ] as IColumn[],
    []
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      handleLoadExtracts(
        1,
        formData.event,
        formData.status !== 'todos' ? formData.status : undefined,
        formData.sports !== 'todos' ? formData.sports : undefined,
        formData.dateStart,
        formData.dateEnd,
        selectedEventId || undefined
      );
    },
    [handleLoadExtracts, selectedEventId]
  );

  const handleWithdraw = useCallback((e) => {
    setWithdraw(
      parseFloat(
        e.target.value.replace('R$', '').replaceAll('.', '').replace(',', '.')
      )
    );
  }, []);

  useEffect(() => {
    if (withdraw !== undefined && withdraw > available) {
      setWithdrawError('Saldo insuficiente');
    } else {
      setWithdrawError('');
    }
  }, [available, withdraw]);

  const handleSubmitWithdraw = useCallback(
    async (data) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: `Gostaria de solicitar o saque?`,

        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then(async (e) => {
        if (e.isConfirmed) {
          setLoading(true);
          try {
            setShowModal(false);
            formRef.current?.setErrors({});

            const schema = Yup.object().shape({
              value: Yup.string().required('O valor é obrigatório'),
            });

            await schema.validate(data, {
              abortEarly: false,
            });

            await api.post('withdrawals/organizers', {
              event_id: selectedEventId,
              value: withdraw,
            });

            setAvailable(available - withdraw);
            setRetained(retained + withdraw);

            handleLoadCashFlow(selectedEventId || undefined);
            handleLoadExtracts(
              1,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              selectedEventId || undefined
            );

            setWithdraw(0);
            setLoading(false);

            Toast.fire({
              icon: 'success',
              iconColor: '#ff6900',
              title: 'Solicitação de saque recebida com sucesso!',
            });
          } catch (error) {
            setWithdraw(0);
            setLoading(false);
            if (error instanceof Yup.ValidationError) {
              const errors = getValidationErros(error);
              formRef.current?.setErrors(errors);
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Não foi possível solicitar o saque!',
              });
            }
          }
        }
      });
    },
    [
      available,
      handleLoadCashFlow,
      handleLoadExtracts,
      retained,
      selectedEventId,
      withdraw,
    ]
  );

  const handleChangePage = useCallback(
    async (page: number) => {
      setPageSelected(page);
      await handleLoadExtracts(
        page,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        selectedEventId || undefined
      );
    },
    [handleLoadExtracts, selectedEventId]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0 w-50">Financeiro</h1>
              <Form
                ref={formRefEvent}
                onSubmit={handleSubmitEvent}
                className="w-75"
              >
                <div className="d-flex justify-content-center align-items-center">
                  <h2 className="h5 mb-0 text-end w-50 me-3">
                    Filtre por evento
                  </h2>
                  <Select
                    name="event"
                    options={eventsModal}
                    placeholder="Selecione o evento"
                    onChangeText={handleChangeTextEvent}
                    value={eventName}
                    onChange={(e: any) => handleSelectEvent(e)}
                    className="bg-white rounded"
                  />
                </div>
              </Form>
            </div>
          </div>

          <div className="col-12 ps-lg-0">
            <div className="row">
              <Balance className="zoom col-12 pe-0">
                <div className="bg-balance p-3 mt-3">
                  <div className="d-flex align-items-center">
                    <div>
                      <span className="h6 text-white fw-normal d-block">
                        Disponível para saque
                      </span>
                      <span className="poppins d-block mb-0 min-w h2 text-white fw-bold">
                        {showValues ? formatPrice(available) : 'R$ -'}
                      </span>
                    </div>
                    <div className="d-flex">
                      <button
                        type="button"
                        onClick={() => setShowValues(!showValues)}
                        className="border-0 bg-transparent mt-4 ms-2"
                      >
                        {showValues ? (
                          <KrEye size={24} color="#FFBB85" />
                        ) : (
                          <KrEyeSlash size={24} color="#FFBB85" />
                        )}
                      </button>
                    </div>
                    {/* <div className="ms-4 ps-2">
                      <span className="h6 text-white fw-normal d-block">
                        Retido
                      </span>
                      <span className="poppins d-block mb-0 h4 text-white">
                        {showValues ? formatPrice(retained) : 'R$ -'}
                      </span>
                    </div> */}
                    <div className="ms-auto">
                      <button
                        type="button"
                        onClick={() => setShowModal(true)}
                        className="btn-withdraw border-0"
                      >
                        <KrWithdraw size={18} color="#fff" /> Sacar
                      </button>
                    </div>
                  </div>
                </div>
              </Balance>
              <Cashflow className="col-12 pe-0 mt-3">
                <div className="bg-white p-3 position-relative">
                  <h2 className="zoom h3 fw-semibold mb-0">
                    {/* <MdAccountBalance
                      size={24}
                      color="#FF6900"
                      className="me-2"
                    /> */}
                    Fluxo de caixa
                  </h2>

                  <hr />

                  <div>
                    <Table
                      className={`${
                        !showValues ? 'd-none' : ''
                      } mb-0 p-0 table`}
                      columns={columns}
                      data={cashesFlow}
                    />
                  </div>
                </div>
              </Cashflow>

              <Extract className="col-12 pe-0 mt-3">
                <div className="bg-white p-3 pb-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <h2 className="h3 fw-semibold mb-0">Extrato</h2>
                    <Filter className="px-0">
                      <FiltersLists
                        onSubmitFilters={handleSubmitFilters}
                        date
                        categories={categories}
                        statusOptions={status}
                      />
                    </Filter>
                  </div>
                  <hr />
                  <div className={`${!showValues ? 'd-none' : 'pb-2'}`}>
                    {extracts.map((item) => (
                      <>
                        <div className="bd d-flex px-3 py-2 my-2">
                          <div className="">
                            <h3 className="h5 fw-semibold mb-0">
                              #{item.number}
                            </h3>
                            <p className="h6 fw-normal mb-0">
                              {item.date} - {item.hour}
                            </p>
                          </div>
                          <div className="w-17 ps-3">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Ação
                            </span>
                            <Action
                              action={item.action}
                              className="h6 fw-normal d-block mb-0"
                            >
                              {item.action}
                            </Action>
                          </div>
                          <div className="w-5">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Doc
                            </span>
                            <span className="gray h6 fw-normal d-block mb-0">
                              {item.orderId}
                            </span>
                          </div>
                          <div className="w-8">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Pagamento
                            </span>
                            <span className="gray h6 fw-normal d-block mb-0">
                              {item.paymentType}
                            </span>
                          </div>
                          <div className="w-10">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Valor
                            </span>
                            <span className="gray h6 fw-normal d-block mb-0">
                              {item.action === 'Transferência realizada' ||
                              item.action === 'Cancelamento realizado' ||
                              item.action === 'Saque de reembolso'
                                ? '-'
                                : ''}{' '}
                              {item.value}
                            </span>
                          </div>
                          <div className="px-3">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Evento
                            </span>
                            <span className="gray h6 fw-normal d-block mb-0">
                              {item.eventId}
                            </span>
                          </div>
                          <div className="w-20">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Nome
                            </span>
                            <span className="gray h6 fw-normal d-block mb-0">
                              {item.orderUserName}
                            </span>
                          </div>
                          <div className="w-10 position-relative">
                            <span className="h6 fw-semibold mb-1 d-block">
                              Saldo total
                              <span
                                className="tooltip-total"
                                data-tooltip="Saldo total: tudo que foi vendido menos o que já foi transferido"
                              >
                                <KrInfo
                                  size={12}
                                  color="#202020"
                                  className="ms-1 mb-1"
                                />
                              </span>
                            </span>
                            <span className="blue h6 fw-normal d-block mb-0">
                              {item.total}
                            </span>
                          </div>
                          <div className="w-15 d-flex align-items-center">
                            <div className="position-relative">
                              <span className="h6 fw-semibold mb-1 d-block">
                                Saldo disponivel
                                <span
                                  className="tooltip-total"
                                  data-tooltip="Saldo dísponivel: valor que pode ser solicitado para transferência"
                                >
                                  <KrInfo
                                    size={12}
                                    color="#202020"
                                    className="ms-1 mb-1"
                                  />
                                </span>
                              </span>
                              <span className="blue h6 fw-normal d-block mb-0">
                                {item.balance}
                              </span>
                            </div>
                            {(item.action === 'Transferência realizada' ||
                              item.action === 'Cancelamento realizado') &&
                              item.receipt && (
                                <a
                                  href={item.receipt}
                                  className="btn-ticket border-0 rounded-circle ms-auto"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img src={ticketSold} alt="Comprovante" />
                                </a>
                              )}
                          </div>
                        </div>
                      </>
                    ))}
                  </div>
                  <Pagination
                    totalPages={totalPages}
                    selectedPage={pageSelected}
                    onChangePage={handleChangePage}
                  />
                </div>
              </Extract>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="zoom modal-filter"
        size="lg"
        centered
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setWithdraw(0);
        }}
      >
        <Form onSubmit={handleSubmitWithdraw}>
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">
              <h2 className="title mb-0">Solicitar saque</h2>
            </Modal.Title>
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setWithdraw(0);
              }}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="container pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <hr className="mb-3" />
                <div className="mb-3 d-flex justify-content-center">
                  <Select
                    name="event"
                    options={eventsModal}
                    placeholder="Selecione o evento para solicitar o saque"
                    onChangeText={handleChangeTextEvent}
                    value={eventName}
                    onChange={(e: any) => handleSelectEvent(e)}
                    className="bg-white rounded"
                  />
                  {/* {erroEvent && (
                      <p className="text-center text-danger">{erroEvent}</p>
                    )} */}
                </div>
                <h3 className="h5 fw-normal mb-4">
                  Disponível para saque: {formatPrice(available)}
                </h3>
                <h4 className="h6 fw-normal">Quanto quer sacar?</h4>
                <div className="d-flex align-items-center">
                  <InputMask
                    placeholder="R$ 0,00"
                    name="value"
                    className="input height"
                    kind="money"
                    value={
                      withdraw ? withdraw?.toFixed(2).toString() : undefined
                    }
                    onChange={handleWithdraw}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setWithdraw(parseFloat(available.toString()))
                    }
                    className="h5 ms-0 btn-filter d-flex align-items-center justify-content-center btn fw-normal px-4 ms-4 mb-0"
                  >
                    Tudo
                  </button>
                </div>
                <p className="text-danger text-center mt-1">{withdrawError}</p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 mt-5 py-4 px-5 d-flex justify-content-end">
            <button
              type="submit"
              disabled={!!withdrawError || eventName === ''}
              className={`${
                withdrawError || eventName === '' ? 'opacity-50' : 'opacity-100'
              } mx-0 btn-filter d-flex align-items-center justify-content-center btn h6 fw-normal py-2 px-4 mt-2 mb-0`}
            >
              Solicitar saque
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Financial;
